import type { ReactNode } from 'react';
import type { Permission } from '@techconnect/shared';
import {
  Home,
  ClipboardList,
  Clock,
  CheckSquare,
  Users,
  BarChart3,
  FileText,
  PenSquare,
  BookOpen,
  ClipboardCheck,
  Box,
  MapPin,
  Map as MapIcon,
  HardHat,
  Calendar,
  Wrench,
  Drill,
  Building2,
  Trophy,
  Award,
  MessageSquare,
  Sparkles,
  Settings as SettingsIcon,
  Plug,
  Shield,
  LayoutGrid,
  Car,
  CalendarClock,
  CalendarRange,
  Gauge,
  ClipboardPen,
  History,
  Eye,
  FlaskConical,
  Bell,
  ReceiptText,
} from 'lucide-react';

/**
 * Navigation configuration. Each entry declares the permission(s) a user
 * needs to see it in the sidebar. AppLayout filters via `can(...)`; the
 * feature matrix further constrains visibility per-tenant at runtime.
 */
export interface NavItem {
  id: string;
  /** English default — actual rendered label comes from `nav.<id>` in i18n */
  label: string;
  /** Short, action-oriented one-liner. Shown on the mobile "More" launcher cards. */
  description: string;
  path: string;
  icon: ReactNode;
  /**
   * Any-of permissions that grant this nav item. Empty array means the item
   * is available to any authenticated user (e.g. Home, Messages, Settings).
   *
   * The API's `NAV_ROLE_SEEDS` (feature-matrix.service.ts) is the source of
   * truth for per-role matrix seeding; this array is the frontend equivalent
   * expressed in permissions, used for sidebar `can(...)` filtering.
   */
  requiredPermissions: Permission[];
  group?: string;
}

/** Lucide icons render at 15×15 in the sidebar (matches Delta). */
const ICON_SIZE = 15;
const I = (
  Component: React.ComponentType<{ size?: number; strokeWidth?: number }>,
) => <Component size={ICON_SIZE} strokeWidth={1.75} />;

// Permission shorthand for readability below.
const ANYONE: Permission[] = [];
const TECH_PLUS: Permission[] = ['workorder.read'];
const SUPERVISOR_PLUS: Permission[] = ['workorder.approve'];
const ADMIN_ONLY: Permission[] = ['admin.system.manage'];
/**
 * Fleet visibility. `requiredPermissions` is any-of, so an LBRE supervisor
 * (who holds `workorder.approve`) and any of the four LBRE fleet roles
 * (who hold `fleet.access`) both pass this gate. Used by the fleet nav
 * group plus the vendors entry that fleet techs need for handoff scheduling.
 */
const FLEET_VISIBLE: Permission[] = ['fleet.access'];

export const NAV_ITEMS: NavItem[] = [
  // ─── Top level (no group) ─────────────────────────────────────────
  { id: 'home', label: 'Home', description: 'Your daily snapshot — what to do next, fast.', path: '/', icon: I(Home), requiredPermissions: ANYONE },
  { id: 'executive', label: 'Executive', description: 'Live operations dashboard for leadership.', path: '/executive', icon: I(Gauge), requiredPermissions: ['executive.view'] },
  { id: 'messages', label: 'Messages', description: 'Channels and direct conversations.', path: '/messages', icon: I(MessageSquare), requiredPermissions: ANYONE },

  // ─── Work ── daily driver items ───────────────────────────────────
  { id: 'work-orders', label: 'Work Orders', description: 'Open jobs, status, hazards, and history.', path: '/work-orders', icon: I(ClipboardList), requiredPermissions: TECH_PLUS, group: 'Work' },
  { id: 'workforce-calendar', label: 'Calendar', description: 'Schedule, leave requests, and overtime tracking.', path: '/workforce-calendar', icon: I(Calendar), requiredPermissions: TECH_PLUS, group: 'Work' },
  { id: 'timecards', label: 'Timecards', description: 'Log hours against work orders or general time.', path: '/timecards', icon: I(Clock), requiredPermissions: TECH_PLUS, group: 'Work' },
  { id: 'invoice-queue', label: 'Invoice Queue', description: 'Three-way match review for vendor invoices.', path: '/invoice-queue', icon: I(FileText), requiredPermissions: SUPERVISOR_PLUS, group: 'Work' },
  { id: 'dispatch', label: 'Dispatch', description: 'Assign work orders to your technicians.', path: '/dispatch', icon: I(Calendar), requiredPermissions: ['workorder.assign'], group: 'Work' },
  { id: 'my-vehicles', label: 'My Vehicles', description: 'Your department vehicles and service status.', path: '/customer/vehicles', icon: I(Car), requiredPermissions: TECH_PLUS, group: 'Work' },
  { id: 'my-approvals', label: 'Pending Approvals', description: 'Repair estimates awaiting your approval.', path: '/customer/approvals', icon: I(ClipboardPen), requiredPermissions: TECH_PLUS, group: 'Work' },
  { id: 'approval-history', label: 'History', description: 'Past approval decisions and service records.', path: '/customer/history', icon: I(History), requiredPermissions: TECH_PLUS, group: 'Work' },

  // ─── People ── crew, vendors ───────────────────────────────────────
  { id: 'crew', label: 'Crew', description: 'Crew roster, availability, certs, and contact info.', path: '/crew', icon: I(Users), requiredPermissions: TECH_PLUS, group: 'People' },
  { id: 'vendors', label: 'Vendors', description: 'Outside contractors, contracts, and contacts.', path: '/vendors', icon: I(Building2), requiredPermissions: FLEET_VISIBLE, group: 'People' },

  // ─── Assets ── equipment, parts, locations ────────────────────────
  { id: 'assets', label: 'Assets', description: 'Equipment lookup, manuals, and service history.', path: '/assets', icon: I(Box), requiredPermissions: TECH_PLUS, group: 'Assets' },
  { id: 'locations', label: 'Locations', description: 'Buildings, rooms, and site information.', path: '/locations', icon: I(MapPin), requiredPermissions: TECH_PLUS, group: 'Assets' },
  { id: 'map', label: 'Map', description: 'Campus map with active jobs and assets.', path: '/map', icon: I(MapIcon), requiredPermissions: TECH_PLUS, group: 'Assets' },
  { id: 'parts', label: 'Parts', description: 'Parts catalog, stock, and reorder.', path: '/parts', icon: I(Wrench), requiredPermissions: TECH_PLUS, group: 'Assets' },
  { id: 'tools', label: 'Tools', description: 'Tool checkout, calibration, and inventory.', path: '/tools', icon: I(Drill), requiredPermissions: TECH_PLUS, group: 'Assets' },

  // ─── Fleet ── vehicle logistics & admin ───────────────────────────
  { id: 'vehicles', label: 'Vehicles', description: 'Fleet vehicles, recalls, and service history.', path: '/fleet/vehicles', icon: I(Car), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'fleet-calendar', label: 'Fleet Calendar', description: 'Staging appointments and vendor scheduling.', path: '/fleet/calendar', icon: I(CalendarClock), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'fleet-dashboards', label: 'Fleet Dashboards', description: 'Out-for-service, overdue WOs, vendor performance.', path: '/fleet/dashboards', icon: I(Gauge), requiredPermissions: ['workorder.approve'], group: 'Fleet' },
  { id: 'approvals-queue', label: 'Approvals Queue', description: 'Customer approval tracking and escalation.', path: '/fleet/approvals', icon: I(ClipboardPen), requiredPermissions: ['workorder.approve'], group: 'Fleet' },
  { id: 'rfq-management', label: 'RFQ Management', description: 'Fleet RFQ lifecycle — estimate authorization, PTA codes, escalation.', path: '/rfq', icon: I(ReceiptText), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'service-windows', label: 'Service Windows', description: 'Vendor service windows, slot offers, and operator bookings.', path: '/fleet/service-windows', icon: I(CalendarRange), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'fleet-appointments', label: 'Appointments', description: 'Coordination threads for staging appointments.', path: '/fleet/appointments', icon: I(CalendarClock), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'service-windows-vendor', label: 'Service Windows', description: 'Confirm or decline vehicle service window requests.', path: '/fleet/service-windows/vendor', icon: I(CalendarRange), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'service-windows-pick', label: 'Book Service Slot', description: 'Browse available service windows and book a slot.', path: '/fleet/service-windows/pick', icon: I(CalendarRange), requiredPermissions: FLEET_VISIBLE, group: 'Fleet' },
  { id: 'fleet-admin', label: 'Fleet Admin', description: 'Insurance, registration, leases, transfers, disposals.', path: '/fleet/admin', icon: I(Shield), requiredPermissions: ADMIN_ONLY, group: 'Fleet' },
  { id: 'lifecycle', label: 'Lifecycle', description: 'Vehicle lifecycle timeline, TCO, and sub-records.', path: '/fleet/lifecycle', icon: I(History), requiredPermissions: ADMIN_ONLY, group: 'Fleet' },
  { id: 'transfers', label: 'Transfers', description: 'Inter-department vehicle transfers and approvals.', path: '/fleet/transfers', icon: I(CalendarClock), requiredPermissions: ADMIN_ONLY, group: 'Fleet' },

  // ─── Insights ── reporting, analytics, comms ──────────────────────
  { id: 'insights', label: 'Insights', description: 'Trends across work orders, time, and teams.', path: '/insights', icon: I(BarChart3), requiredPermissions: ['insights.read'], group: 'Insights' },
  { id: 'reports', label: 'Reports', description: 'Saved reports and exports for stakeholders.', path: '/reports', icon: I(FileText), requiredPermissions: ['insights.read'], group: 'Insights' },
  { id: 'finance', label: 'Finance Console', description: 'LBRE billing — statements, chargeback recovery, corrections, close.', path: '/finance', icon: I(ReceiptText), requiredPermissions: ['billing.access'], group: 'Insights' },
  { id: 'communications', label: 'Comms Drafter', description: 'AI-assisted updates for tenants and stakeholders.', path: '/communications', icon: I(PenSquare), requiredPermissions: ['content.write'], group: 'Insights' },
  { id: 'leaderboard', label: 'Leaderboard', description: 'Friendly competition across crews.', path: '/leaderboard', icon: I(Trophy), requiredPermissions: TECH_PLUS, group: 'Insights' },
  { id: 'kudos', label: 'Kudos', description: 'Recognize great work from teammates.', path: '/kudos', icon: I(Award), requiredPermissions: TECH_PLUS, group: 'Insights' },

  // ─── Configuration ── system administration ────────────────────────
  { id: 'ai-hub', label: 'AI Hub', description: 'All AI tools in one place — ask, generate, analyze, automate.', path: '/ai', icon: I(Sparkles), requiredPermissions: ADMIN_ONLY, group: 'Config' },
  { id: 'pm-schedules', label: 'PM Schedules', description: 'Recurring preventive maintenance work order schedules.', path: '/pm-schedules', icon: I(Calendar), requiredPermissions: ['automations.read'], group: 'Config' },
  { id: 'automations', label: 'Automations', description: 'Scheduled jobs and on-demand automation tasks.', path: '/automations', icon: I(Sparkles), requiredPermissions: ['automations.read'], group: 'Config' },
  { id: 'dashboard-editor', label: 'Dashboard Editor', description: 'Configure home page widgets per role.', path: '/dashboard-editor', icon: I(SettingsIcon), requiredPermissions: ADMIN_ONLY, group: 'Config' },
  { id: 'impersonate', label: 'Impersonate', description: 'View the app as another user (read-only).', path: '/impersonate', icon: I(Eye), requiredPermissions: ADMIN_ONLY, group: 'Config' },
  { id: 'howto', label: 'How-To Drafts', description: 'Capture and refine standard operating procedures.', path: '/howto', icon: I(BookOpen), requiredPermissions: ['content.write'], group: 'Config' },
  { id: 'forms', label: 'Forms', description: 'Inspection and compliance forms by trade.', path: '/forms', icon: I(ClipboardCheck), requiredPermissions: ['content.write'], group: 'Config' },
  { id: 'notification-center', label: 'Notifications', description: 'Configure notification triggers, channels, and templates.', path: '/config/notifications', icon: I(Bell), requiredPermissions: ADMIN_ONLY, group: 'Config' },
  { id: 'integrations', label: 'Integrations', description: 'Maximo, SSO, and other system connections.', path: '/integrations', icon: I(Plug), requiredPermissions: ['integrations.read'], group: 'Config' },
  { id: 'admin', label: 'Console', description: 'Users, role matrix, schemas, and audit log.', path: '/admin', icon: I(Shield), requiredPermissions: ADMIN_ONLY, group: 'Config' },
  { id: 'dev-hub', label: 'Dev Hub', description: 'Developer tools, component library, and design references.', path: '/dev', icon: I(FlaskConical), requiredPermissions: ADMIN_ONLY, group: 'Config' },

  // ─── Preferences ── user-facing settings ──────────────────────────
  { id: 'help', label: 'Help Center', description: 'Interactive workflow guides and role spotlights.', path: '/help', icon: I(BookOpen), requiredPermissions: ANYONE, group: 'Preferences' },
  { id: 'whats-new', label: "What's New", description: 'Recent platform updates and release notes.', path: '/whats-new', icon: I(Sparkles), requiredPermissions: TECH_PLUS, group: 'Preferences' },
  { id: 'settings', label: 'Settings', description: 'Profile, notifications, and preferences.', path: '/settings', icon: I(SettingsIcon), requiredPermissions: ANYONE, group: 'Preferences' },
];

/**
 * Synthetic mobile-only "More" tab — never in NAV_ITEMS, not a real route in
 * the sidebar. Always occupies the 5th tab slot on mobile and links to the
 * full app launcher at `/more`.
 */
export const MORE_TAB: { id: 'more'; label: string; description: string; path: string; icon: ReactNode } = {
  id: 'more',
  label: 'More',
  description: 'All apps available to you.',
  path: '/more',
  icon: I(LayoutGrid),
};

/**
 * Mobile bottom tab bar — exactly 4 items per role. The 5th slot is always
 * the synthetic `MORE_TAB` (added by AppLayout), which opens the launcher.
 * Notifications lives in the Topbar bell, so it's intentionally absent here.
 */
/**
 * Mobile bottom tab bar — 3 role-specific items + Home (always first) +
 * More (always last, appended by AppLayout). Total = 5 tappable slots.
 *
 * Chosen by frequency-of-use for each role's daily workflow.
 */
export const MOBILE_TAB_IDS: Record<string, string[]> = {
  technician: ['home', 'work-orders', 'timecards', 'messages'],
  supervisor: ['home', 'work-orders', 'workforce-calendar', 'approvals'],
  admin: ['home', 'work-orders', 'fleet-admin', 'invoice-queue'],
  sysadmin: ['home', 'work-orders', 'fleet-admin', 'invoice-queue'],
  vendor_admin: ['home', 'dispatch', 'work-orders', 'messages'],
  vendor_supervisor: ['home', 'dispatch', 'work-orders', 'messages'],
  vendor_tech: ['home', 'work-orders', 'scan', 'messages'],
  vendor_contact: ['home', 'work-orders', 'messages'],
  customer_operator: ['home', 'my-vehicles', 'my-approvals', 'approval-history'],
};

/**
 * Nav items available when OFFLINE. Only pages that work with cached data.
 * When online, the full nav (permissions + feature matrix) applies.
 */
export const OFFLINE_NAV_IDS: ReadonlySet<string> = new Set([
  'home',
  'work-orders',
  'timecards',
  'forms',
  'settings',
]);
